import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { getCampaignReport, getCampaignStateContacts, exportCampaignStateContacts } from '../../mailTemplate/src/services/B2BServices'
import { useToast } from '../hooks/useToast'

import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip
} from '@mui/material'
import {
  Inbox,
  Schedule,
  Send,
  Refresh,
  Close
} from '@mui/icons-material'
import { PieChart } from '@mui/x-charts/PieChart'
import ExcelIcon from '../../components/icon/ExcelIcon'
import McPageHelpers from '../../pages/mc-components/McPageHelpers'

const StatItem = ({ icon, title, value, color = 'primary.main', isLoading = false }) => (
  <Box sx={{ textAlign: 'center', p: 1.5 }}>
    <Box
      sx={{
        mb: 1,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="h5"
      component="div"
      sx={{
        fontWeight: 600,
        mb: 0.5,
        color: 'text.primary',
        display: 'flex',
        justifyContent: 'center'
      }}
    >
      {isLoading ? <Skeleton width={45} /> : (value !== null && value !== undefined ? value : '-')}
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ fontWeight: 400 }}
    >
      {title}
    </Typography>
  </Box>
)

const PieChartSkeleton = ({ size = 320 }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
    <Skeleton variant="circular" width={size} height={size} />

    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, maxWidth: 500 }}>
      {[...Array(4)].map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            borderRadius: 1
          }}
        >
          <Skeleton variant="circular" width={12} height={12} sx={{ mr: 1 }} />
          <Skeleton width={60} height={14} sx={{ mr: 1 }} />
          <Skeleton width={20} height={14} sx={{ mr: 0.5 }} />
          <Skeleton width={35} height={12} />
        </Box>
      ))}
    </Box>
  </Box>
)

const EmptyPieChart = ({ size = 320 }) => {
  const { t } = useTranslation()

  const emptyData = [{
    id: 0,
    value: 1,
    label: '',
    color: '#e0e0e0'
  }]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 4 }}>
      <Box sx={{ position: 'relative' }}>
        <PieChart
          series={[
            {
              data: emptyData,
              cx: size / 2,
              cy: size / 2,
              outerRadius: size / 2 - 20,
              color: '#e0e0e0'
            }
          ]}
          colors={['#e0e0e0']}
          width={size}
          height={size}
          slotProps={{
            legend: { hidden: true },
            pieArc: {
              style: {
                pointerEvents: 'none',
                fill: '#e0e0e0'
              }
            }
          }}
          sx={{
            '& .MuiPieArc-root': {
              fill: '#e0e0e0 !important'
            }
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 1
          }}
        >
          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
            {t('mailTemplate.campaigns.detail.noEmailStatsAvailable')}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export const EmailStatsPieChart = ({ data, size = 320, onSliceClick, selectedKey }) => {
  const colorMap = {
    WAITING: 'rgb(205, 205, 202)',
    FAILED: 'rgb(170, 108, 99)',
    DELIVERED: 'rgb(95, 125, 165)',
    OPENED: 'rgb(166, 172, 108)',
    CLICKED: 'rgb(120, 150, 100)',
    REJECTED: 'rgb(130, 75, 70)'
  }

  const total = data.reduce((sum, item) => sum + item.value, 0)

  const chartData = data.map((item, index) => {
    const percentage = (item.value / total) * 100
    return {
      id: index,
      value: item.value,
      label: item.category,
      color: colorMap[item.key] || '#666',
      percentage,
      key: item.key
    }
  })

  const chartColors = chartData.map(item => item.color)

  const handleItemClick = (_event, pieItemIdentifier, _item) => {
    if (onSliceClick && pieItemIdentifier?.dataIndex !== undefined) {
      const clickedItem = chartData[pieItemIdentifier.dataIndex]
      if (clickedItem) {
        onSliceClick(clickedItem.key)
      }
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      <Box sx={{ cursor: 'pointer' }}>
        <PieChart
          series={[
            {
              cx: size / 2,
              cy: size / 2,
              data: chartData,
              arcLabelMinAngle: 10,
              outerRadius: size / 2 - 20,
              highlightScope: { faded: 'global', highlighted: 'item' },
              arcLabel: (item) => item.percentage >= 10 ? item.label : '',
              faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' }
            }
          ]}
          colors={chartColors}
          width={size}
          height={size}
          onItemClick={handleItemClick}
          slotProps={{
            legend: { hidden: true }
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, maxWidth: 500 }}>
        {data.map((item, index) => {
          const percentage = ((item.value / total) * 100).toFixed(1)
          const isSelected = selectedKey === item.key

          return (
            <Box
              key={index}
              onClick={() => onSliceClick && onSliceClick(item.key)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1,
                borderRadius: 1,
                cursor: onSliceClick ? 'pointer' : 'default',
                backgroundColor: isSelected ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                border: isSelected ? '1px solid rgba(33, 150, 243, 0.5)' : '1px solid transparent',
                '&:hover': onSliceClick
                  ? {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  : {}
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: colorMap[item.key] || '#666',
                  borderRadius: '50%',
                  mr: 1
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  mr: 1,
                  fontSize: '0.875rem'
                }}
              >
                {item.category}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  mr: 0.5,
                  fontSize: '0.875rem'
                }}
              >
                {item.value}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: '0.75rem'
                }}
              >
                ({percentage}%)
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

const getFirstWebsite = (web) => {
  if (!web) return null
  const websites = web.split(/[,;]/).map(w => w.trim()).filter(w => w)
  if (websites.length === 0) return null
  const firstWebsite = websites[0]
  return firstWebsite.indexOf('://') === -1 ? `http://${firstWebsite}` : firstWebsite
}

const StateContactsTable = ({ data, loading, selectedState, onClose, campaignId, t }) => {
  const { i18n } = useTranslation()
  const [page, setPage] = useState(0)
  const [rowsPerPage] = useState(10)
  const [isExporting, setIsExporting] = useState(false)

  const stateLabel = t(`mailTemplate.campaigns.detail.emailStatus.${selectedState}`)
  const dateLocale = McPageHelpers.localeMap[i18n.language] || 'en-US'

  const formatCreatedAt = (value) => {
    if (!value) return '—'

    const date = new Date(value)

    if (isNaN(date.getTime())) return '—'

    return date.toLocaleString(dateLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleExportExcel = async () => {
    try {
      setIsExporting(true)
      const res = await exportCampaignStateContacts(campaignId, selectedState)

      const blob = new Blob([res.data], { type: 'application/octet-stream' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `campaign-${selectedState.toLowerCase()}-contacts.xlsx`
      a.click()

      URL.revokeObjectURL(url)
    } catch {
      // error handled by B2BAPI interceptor
    } finally {
      setIsExporting(false)
    }
  }

  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const getEmails = (emailsContainer) => {
    if (!emailsContainer) return []
    try {
      const parsed = typeof emailsContainer === 'string' ? JSON.parse(emailsContainer) : emailsContainer
      const emails = parsed?.emails || parsed
      if (Array.isArray(emails) && emails.length > 0) {
        return emails.map(e => e.email || e).filter(Boolean)
      }
      return []
    } catch {
      return []
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        borderRadius: 2.5,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'rgba(255,255,255,0.95)'
      }}
    >
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pl: 3,
        pr: 2,
        py: 1.5,
        bgcolor: '#f4f6fa',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600} color="#334155">
            {t('mailTemplate.campaignReport.stateTable.title', { state: stateLabel })}
          </Typography>
          <Typography
            component="span"
            variant="caption"
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: 'primary.main',
              color: 'white',
              fontWeight: 600
            }}
          >
            {data.length}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            onClick={handleExportExcel}
            disabled={isExporting || loading || data.length === 0}
            size="small"
            sx={{ opacity: (isExporting || loading || data.length === 0) ? 0.4 : 1 }}
          >
            <ExcelIcon color="#64748b" />
          </IconButton>
          <IconButton onClick={onClose} size="small" sx={{ color: '#64748b' }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {loading
        ? (
        <Box sx={{ p: 3 }}>
          {[...Array(5)].map((_, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Skeleton variant="text" width="20%" height={32} />
              <Skeleton variant="text" width="15%" height={32} />
              <Skeleton variant="text" width="25%" height={32} />
              <Skeleton variant="text" width="25%" height={32} />
              <Skeleton variant="text" width="15%" height={32} />
            </Box>
          ))}
        </Box>
          )
        : data.length === 0
          ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
          {t('mailTemplate.campaignReport.stateTable.noData')}
        </Typography>
            )
          : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      fontWeight: 600,
                      bgcolor: '#f8fafc',
                      color: '#475569',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 1.5
                    }
                  }}
                >
                  <TableCell>{t('mailTemplate.campaignReport.stateTable.contactName')}</TableCell>
                  <TableCell>{t('mailTemplate.campaignReport.stateTable.position')}</TableCell>
                  <TableCell>{t('mailTemplate.campaignReport.stateTable.firmName')}</TableCell>
                  <TableCell>{t('mailTemplate.campaignReport.stateTable.email')}</TableCell>
                  <TableCell>{t('mailTemplate.campaignReport.stateTable.createdAt')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  '& td': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: 2.5
                  },
                  '& tr:hover': { bgcolor: 'rgba(2,132,199,0.04)' },
                  '& tr:last-child td': { borderBottom: 'none' }
                }}
              >
                {paginatedData.map((contact, index) => {
                  const fullName = `${contact.contactFirstName || ''} ${contact.contactLastName || ''}`.trim()
                  const website = getFirstWebsite(contact.firmWebsite)
                  const emails = getEmails(contact.contactEmailsContainer)
                  const visibleEmails = emails.slice(0, 3)
                  const hiddenEmails = emails.slice(3)

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} color="#1e293b">
                          {fullName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="#64748b">
                          {contact.contactPosition || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {contact.firmName
                          ? (
                              website
                                ? (
                              <Link
                                href={website}
                                target="_blank"
                                rel="noreferrer"
                                underline="hover"
                                sx={{
                                  color: '#1e293b !important',
                                  fontWeight: 500,
                                  '&:visited': { color: '#1e293b !important' },
                                  '&:hover': { color: '#0284c7 !important' }
                                }}
                              >
                                {contact.firmName}
                              </Link>
                                  )
                                : (
                              <Typography variant="body2" color="#1e293b">{contact.firmName}</Typography>
                                  )
                            )
                          : (
                          <Typography variant="body2" color="#94a3b8">—</Typography>
                            )}
                      </TableCell>
                      <TableCell>
                        {emails.length > 0
                          ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {visibleEmails.map((email, i) => (
                              <Link
                                key={i}
                                href={`mailto:${email}`}
                                underline="hover"
                                sx={{
                                  color: '#1e293b !important',
                                  fontSize: '0.875rem',
                                  '&:visited': { color: '#1e293b !important' },
                                  '&:hover': { color: '#0284c7 !important' }
                                }}
                              >
                                {email}
                              </Link>
                            ))}
                            {hiddenEmails.length > 0 && (
                              <Tooltip
                                title={
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {hiddenEmails.map((email, i) => (
                                      <span key={i}>{email}</span>
                                    ))}
                                  </Box>
                                }
                                arrow
                                placement="top"
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#0284c7',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                >
                                  +{hiddenEmails.length} more
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                            )
                          : (
                          <Typography variant="body2" color="#94a3b8">—</Typography>
                            )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="#64748b">
                          {formatCreatedAt(contact.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {data.length > rowsPerPage && (
            <TablePagination
              component="div"
              count={data.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10]}
              labelDisplayedRows={({ from, to, count, page: currentPage }) => {
                const totalPages = Math.ceil(count / rowsPerPage)
                return t('mailTemplate.campaignReport.stateTable.pagination', {
                  from,
                  to,
                  count,
                  page: currentPage + 1,
                  totalPages
                })
              }}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: '#f8fafc',
                '& .MuiTablePagination-toolbar': {
                  alignItems: 'center'
                },
                '& .MuiTablePagination-displayedRows': {
                  margin: 0
                },
                '& .MuiTablePagination-actions': {
                  marginLeft: 1
                }
              }}
            />
          )}
        </>
            )}
    </Paper>
  )
}

const CampaignStats = ({ campaign }) => {
  const { t } = useTranslation()

  const { showError } = useToast()

  const [statsData, setStatsData] = useState(null)
  const [isLoadingStats, setIsLoadingStats] = useState(false)

  const [selectedState, setSelectedState] = useState(null)
  const [stateContacts, setStateContacts] = useState([])
  const [loadingContacts, setLoadingContacts] = useState(false)

  const formatDate = (dateString) => {
    if (isLoadingStats) return null

    if (!dateString) return t('mailTemplate.campaigns.detail.neverChecked')

    return new Date(dateString).toLocaleDateString()
  }

  const sentEmailCount = useMemo(() => {
    if (!statsData?.outgoingEmailStats) return null

    const stats = statsData.outgoingEmailStats
    const total = Object.values(stats).reduce((sum, count) => sum + (count || 0), 0)

    return total
  }, [statsData])

  const incomingEmailCount = useMemo(() => {
    if (!statsData) return null

    const count = statsData.incomingEmailCount

    return (count !== null && count !== undefined) ? count : null
  }, [statsData])

  const hasEmailStats = useMemo(() => {
    if (isLoadingStats) return true

    const stats = statsData?.outgoingEmailStats || {}

    return Object.values(stats).some(value => value > 0)
  }, [isLoadingStats, statsData?.outgoingEmailStats])

  const fetchStatsData = useCallback(async () => {
    if (!campaign?.id) return

    try {
      setIsLoadingStats(true)
      const reportResponse = await getCampaignReport(campaign.id)
      setStatsData(reportResponse.data)
    } catch (error) {
      showError(t('mailTemplate.campaigns.errors.failedToLoadCampaignStats'))
    } finally {
      setIsLoadingStats(false)
    }
  }, [campaign?.id, showError, t])

  const prepareChartData = () => {
    const stats = statsData?.outgoingEmailStats || {}

    return Object.entries(stats)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => ({
        category: t(`mailTemplate.campaigns.detail.emailStatus.${key}`),
        value,
        key
      }))
  }

  const handleSliceClick = useCallback(async (stateKey) => {
    if (selectedState === stateKey) {
      setSelectedState(null)
      setStateContacts([])
      return
    }

    setSelectedState(stateKey)
    setLoadingContacts(true)
    setStateContacts([])

    try {
      const response = await getCampaignStateContacts(campaign.id, stateKey)
      setStateContacts(response.data || [])
    } catch (error) {
      console.error('Failed to fetch state contacts:', error)
      setStateContacts([])
    } finally {
      setLoadingContacts(false)
    }
  }, [campaign?.id, selectedState])

  const handleCloseTable = useCallback(() => {
    setSelectedState(null)
    setStateContacts([])
  }, [])

  useEffect(() => {
    if (campaign?.id && !statsData && !isLoadingStats) {
      fetchStatsData()
    }
  }, [campaign?.id, statsData, isLoadingStats, fetchStatsData])

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {t('mailTemplate.campaigns.detail.campaignStatistics')}
        </Typography>
        <IconButton
          onClick={fetchStatsData}
          disabled={isLoadingStats}
          sx={{
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'rgba(33, 150, 243, 0.1)'
            }
          }}
        >
          <Refresh />
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
              borderRadius: 3,
              border: '1px solid rgba(33, 150, 243, 0.2)'
            }}
          >
            <StatItem
              icon={<Send sx={{ fontSize: 36 }} />}
              title={t('mailTemplate.campaigns.detail.sentEmails')}
              value={sentEmailCount}
              color="primary.main"
              isLoading={isLoadingStats}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              borderRadius: 3,
              border: '1px solid rgba(76, 175, 80, 0.2)'
            }}
          >
            <StatItem
              icon={<Inbox sx={{ fontSize: 36 }} />}
              title={t('mailTemplate.campaigns.detail.incomingEmails')}
              value={incomingEmailCount}
              color="success.main"
              isLoading={isLoadingStats}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              borderRadius: 3,
              border: '1px solid rgba(255, 152, 0, 0.2)'
            }}
          >
            <StatItem
              icon={<Schedule sx={{ fontSize: 36 }} />}
              title={t('mailTemplate.campaigns.detail.lastInboxCheck')}
              value={formatDate(statsData?.inboxLastChecked)}
              color="warning.main"
              isLoading={isLoadingStats}
            />
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
          {t('mailTemplate.campaigns.detail.emailStatusDistribution')}
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: 'rgba(245, 245, 245, 0.3)',
            borderRadius: 3,
            border: '1px solid rgba(0, 0, 0, 0.1)'
          }}
        >
          {isLoadingStats
            ? <PieChartSkeleton />
            : hasEmailStats
              ? <EmailStatsPieChart
                  data={prepareChartData()}
                  onSliceClick={handleSliceClick}
                  selectedKey={selectedState}
                />
              : <EmptyPieChart />
          }
        </Paper>

        {selectedState && (
          <StateContactsTable
            data={stateContacts}
            loading={loadingContacts}
            selectedState={selectedState}
            onClose={handleCloseTable}
            campaignId={campaign.id}
            t={t}
          />
        )}
      </Box>

    </Box>
  )
}

export default CampaignStats
